import { useState, useEffect, useContext, forwardRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { enGB } from 'date-fns/locale';
import { Add, Delete } from '@mui/icons-material';
import { db, storage } from '../firebase'; // Adjust the import based on your project structure
import { collection, getDocs, query, where, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import AppContext from '../AppContext';

  // Use forwardRef to wrap the component
const EditOrderModal = forwardRef(({ selectedOrder, setEditModalOpen, handleSaveEdit }, ref) => {
    const [products, setProducts] = useState([]);
    const { shopId, isOnShift, credit, userId, userName, saleTax, t } = useContext(AppContext);
    const [validationError, setValidationError] = useState(null);    useEffect(() => {
        const fetchProducts = async () => {
            try {
            const productsRef = collection(db, "products"); // Adjust collection name as needed
            const q = query(productsRef, where("productType", "==", "Order"), where("shopId", "==", shopId)); // Add where clause
            const querySnapshot = await getDocs(q);
            const productsData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setProducts(productsData);
            console.log("Products fetched:", productsData);
            } catch (error) {
            console.error("Error fetching products:", error);
            }
        };
        fetchProducts();
    }, [shopId]);

    // Separate existing payments and new payments
    const [existingPayments, setExistingPayments] = useState(
        selectedOrder.payments?.length > 0 ? [...selectedOrder.payments] : []
    );
    
    // New payments start as an empty array
    const [newPayments, setNewPayments] = useState([]);

    // Convert Firestore Timestamps to Date objects
    const fulfillmentDate = selectedOrder.fulfillmentDate ? 
        (selectedOrder.fulfillmentDate.toDate ? 
            selectedOrder.fulfillmentDate.toDate() : 
            new Date(selectedOrder.fulfillmentDate)) : 
        null;

    const fulfillmentTime = selectedOrder.fulfillmentTime ? 
        (selectedOrder.fulfillmentTime.toDate ? 
            selectedOrder.fulfillmentTime.toDate() : 
            new Date(selectedOrder.fulfillmentTime)) : 
        null;

    const [orderData, setOrderData] = useState({
        ...selectedOrder,
        cartItems: selectedOrder.cartItems || [],
        orderNotes: selectedOrder.orderNotes || '',
        inscriptionText: selectedOrder.inscription || '',
        imageUrls: selectedOrder.imageUrls || [],
        paymentAmount: selectedOrder.paymentAmount || 0,
        fulfillmentDate,
        fulfillmentTime
    });

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * (saleTax / 100);
    // Include delivery fee in total if present
    const deliveryFee = orderData.deliveryFeeAmount || 0;
    // Include discount in total if present
    const discount = orderData.discount || 0;
    const total = subtotal + tax + deliveryFee - discount;
    
    // Calculate total from both existing and new payments
    const existingTotal = existingPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const newTotal = newPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const totalPaid = existingTotal + newTotal;
    
    const balance = total - totalPaid;
    return { subtotal, tax, total, balance, totalPaid };
  };

  const [totals, setTotals] = useState(calculateTotals(orderData.cartItems));

  // Update totals whenever cartItems or payments change
  useEffect(() => {
    setTotals(calculateTotals(orderData.cartItems));
  }, [orderData.cartItems, existingPayments, newPayments]);

  const paymentMethods = ['Cash', 'Kpay', 'WavePay'];

  const handleAddItem = () => {
    setOrderData({
      ...orderData,
      cartItems: [...orderData.cartItems, {
        category: '',
        name: '',
        price: 0,
        productType: '',
        quantity: 1,
        selectedVariant: '',
        totalPrice: 0,
        variantPrice: 0
      }]
    });
  };

  const handleDeleteItem = (index) => {
    const newItems = orderData.cartItems.filter((_, i) => i !== index);
    setOrderData({ ...orderData, cartItems: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...orderData.cartItems];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'name') {
      const product = products.find(p => p.name === value);
      if (product) {
        newItems[index] = {
          ...newItems[index],
          category: product.category,
          price: product.price,
          productType: product.productType,
          selectedVariant: '',
          variantPrice: 0
        };
      }
    }

    if (field === 'selectedVariant') {
      const product = products.find(p => p.name === newItems[index].name);
      const variant = product?.variants.find(v => v.name === value);
      newItems[index].variantPrice = variant?.price || 0;
    }

    newItems[index].totalPrice = 
      (newItems[index].price + newItems[index].variantPrice) * newItems[index].quantity;

    setOrderData({ ...orderData, cartItems: newItems });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    const uploadPromises = files.map(async (file) => {
        const storageRef = ref(storage, `orders/${selectedOrder.orderID}/${file.name}-${Date.now()}`); // Unique path
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        return downloadURL;
    });

    try {
        const newImageUrls = await Promise.all(uploadPromises);
        setOrderData({
            ...orderData,
            imageUrls: [...orderData.imageUrls, ...newImageUrls]
        });
    } catch (error) {
        console.error("Error uploading images:", error);
    }
  };

  const handleDeleteImage = async (index) => {
    const imageUrlToDelete = orderData.imageUrls[index];
    const storageRef = ref(storage, imageUrlToDelete); // Create reference from URL

    try {
        await deleteObject(storageRef); // Delete from Firebase Storage
        const updatedImageUrls = orderData.imageUrls.filter((_, i) => i !== index);
        setOrderData({
            ...orderData,
            imageUrls: updatedImageUrls
        });
    } catch (error) {
        console.error("Error deleting image:", error);
    }
  };

  // Handle new payment changes
  const handleNewPaymentChange = (index, field, value) => {
    const updatedPayments = [...newPayments];
    if (field === 'amount') {
      updatedPayments[index][field] = parseFloat(value) || 0;
    } else {
      updatedPayments[index][field] = value;
    }
    setNewPayments(updatedPayments);
  };

  // Handle removing existing payment
  const removeExistingPayment = (index) => {
    const updatedPayments = existingPayments.filter((_, i) => i !== index);
    setExistingPayments(updatedPayments);
  };

  // Handle removing new payment
  const removeNewPayment = (index) => {
    const updatedPayments = newPayments.filter((_, i) => i !== index);
    setNewPayments(updatedPayments);
  };

  // Add new payment method
  const addPaymentMethod = () => {
    setNewPayments([...newPayments, { method: 'Cash', amount: 0 }]);
  };

  const formatToMyanmarCurrency = (number) => {
    return new Intl.NumberFormat('my-MM').format(number) + ' MMK';
  };

    const validateAndSave = () => {
    // Basic validation
    if (orderData.cartItems.length === 0) {
      setValidationError("Order must contain at least one item");
      return;
    }

    // Validate cart items have names
    const invalidItems = orderData.cartItems.filter(item => !item.name);
    if (invalidItems.length > 0) {
      setValidationError("All items must have a product selected");
      return;
    }

    // Validate fulfillment date and time
    if (!orderData.fulfillmentDate || !orderData.fulfillmentTime) {
      setValidationError("Fulfillment date and time are required");
      return;
    }

    // Ensure dates are valid Date objects
    const fulfillmentDate = orderData.fulfillmentDate instanceof Date ? 
        orderData.fulfillmentDate : 
        (orderData.fulfillmentDate?.toDate ? 
            orderData.fulfillmentDate.toDate() : 
            new Date(orderData.fulfillmentDate));
    
    const fulfillmentTime = orderData.fulfillmentTime instanceof Date ? 
        orderData.fulfillmentTime : 
        (orderData.fulfillmentTime?.toDate ? 
            orderData.fulfillmentTime.toDate() : 
            new Date(orderData.fulfillmentTime));

    // Update orderData with validated dates
    const validatedOrderData = {
        ...orderData,
        fulfillmentDate,
        fulfillmentTime
    };

    // Calculate total due
    const { total, totalPaid } = calculateTotals(orderData.cartItems);    // Validate payment amount doesn't exceed total
    if (totalPaid > total) {
      setValidationError(`Payment amount (${formatToMyanmarCurrency(totalPaid)}) exceeds total due (${formatToMyanmarCurrency(total)})`);
      return;
    }

    // Prepare final order data with calculated totals
    const finalOrderData = {
      ...orderData,
      subtotal: totals.subtotal,
      tax: totals.tax,
      total: totals.total,
      inscription: orderData.inscriptionText,
      payments: [...existingPayments, ...newPayments],
      paymentAmount: totalPaid
    };

    // Clear any validation errors
    setValidationError(null);
    
    // Ensure dates are properly formatted for Firestore
    const finalDataWithDates = {
      ...finalOrderData,
      fulfillmentDate: validatedOrderData.fulfillmentDate,
      fulfillmentTime: validatedOrderData.fulfillmentTime
    };

    // Call the save handler with payment information for cashbook updates
    handleSaveEdit(finalDataWithDates, {
      originalPayments: selectedOrder.payments || [],
      newPayments: [...existingPayments, ...newPayments]
    });
  };

  return (
    <Box sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      maxWidth: 800,
      bgcolor: 'background.paper',
      boxShadow: 24,
      p: 4,
      maxHeight: '90vh',
      overflowY: 'auto',
    }}>
      {selectedOrder && (
        <Box component="form">
          <Typography variant="h6" mb={2}>Order #{selectedOrder.orderID}</Typography>

          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}
          
          {/* Fulfillment Date and Time */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t("Fulfillment Date")}
                  value={orderData.fulfillmentDate instanceof Date ? orderData.fulfillmentDate : null}
                  onChange={(newDate) => {
                    setOrderData({
                      ...orderData,
                      fulfillmentDate: newDate
                    });
                  }}
                  minDate={new Date()}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      required: true,
                      error: !!validationError && !orderData.fulfillmentDate,
                      helperText: validationError && !orderData.fulfillmentDate ? "Fulfillment date is required" : ""
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label={t("Fulfillment Time")}
                  value={orderData.fulfillmentTime instanceof Date ? orderData.fulfillmentTime : null}
                  onChange={(newTime) => {
                    setOrderData({
                      ...orderData,
                      fulfillmentTime: newTime
                    });
                  }}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      required: true,
                      error: !!validationError && !orderData.fulfillmentTime,
                      helperText: validationError && !orderData.fulfillmentTime ? "Fulfillment time is required" : ""
                    } 
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          {/* Cart Items Table */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Product")}</TableCell>
                <TableCell>{t("Variant")}</TableCell>
                <TableCell>{t("Qty")}</TableCell>
                <TableCell></TableCell>               
              </TableRow>
            </TableHead>
            <TableBody>
              {orderData.cartItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Autocomplete
                      options={products.map(p => p.name)}
                      value={item.name}
                      onChange={(e, value) => handleItemChange(index, 'name', value)}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={
                        products.find(p => p.name === item.name)?.variants?.map(v => v.name) || []
                      }
                      value={item.selectedVariant}
                      onChange={(e, value) => handleItemChange(index, 'selectedVariant', value)}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                      size="small"
                      sx={{ width: 50 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteItem(index)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<Add />} onClick={handleAddItem} sx={{ mt: 1 }}>
            Add Item
          </Button>

          <TextField
            label={t("Inscription Text")}
            fullWidth
            margin="normal"
            value={orderData.inscriptionText}
            onChange={(e) => setOrderData({ ...orderData, inscriptionText: e.target.value })}
          />
           <TextField
            label={t("Order Notes")}
            fullWidth
            margin="normal"
            value={orderData.orderNotes}
            onChange={(e) => setOrderData({ ...orderData, orderNotes: e.target.value })}
          />

        {/* Image Upload Section */}
        <Box mt={2}>
            <input
                accept="image/*"
                style={{ display: 'none' }}
                id="raised-button-file"
                multiple
                type="file"
                onChange={handleImageUpload}
            />
            <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" disableElevation>
                    Insert Images
                </Button>
            </label>
            {orderData.imageUrls && orderData.imageUrls.length > 0 && (
            <Box mt={2}>
                {orderData.imageUrls.map((imageUrl, index) => (
                    <Box key={index} mt={2} mr={2} sx={{ position: 'relative', display: 'inline-block' }}>
                        <img 
                            src={imageUrl} 
                            alt=""
                            style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                        />
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                },
                                color: 'red'
                            }}
                            onClick={() => handleDeleteImage(index)}
                            size="small"
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        )}
        </Box>

        {/* Payment Section */}
        <Box mt={2}>
            <Typography variant="h6">{t("Payment Details")}</Typography>
            
            {/* Existing Payments - Read-only with delete option */}
            {existingPayments.length > 0 && (
                <Box mt={1} mb={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t("Existing Payments")}</Typography>
                    {existingPayments.map((payment, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip 
                                label={`${payment.method}: ${formatToMyanmarCurrency(payment.amount)}`}
                                sx={{ mr: 1 }}
                            />
                            <IconButton 
                                onClick={() => removeExistingPayment(index)} 
                                size="small"
                                color="error"
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
            
            {/* New Payments - Fully editable */}
            {newPayments.length > 0 && (
                <Box mt={1} mb={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t("New Payments")}</Typography>
                    {newPayments.map((payment, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TextField
                                select
                                label={t("Payment Method")}
                                value={payment.method}
                                onChange={(e) => handleNewPaymentChange(index, 'method', e.target.value)}
                                sx={{ width: 200, mr: 2 }}
                            >
                                {paymentMethods.map((method) => (
                                    <MenuItem key={method} value={method}>{method}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label={t("Amount")}
                                type="number"
                                value={payment.amount}
                                onChange={(e) => handleNewPaymentChange(index, 'amount', e.target.value)}
                                sx={{ width: 150, mr: 2 }}
                            />
                            <IconButton onClick={() => removeNewPayment(index)}>
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
            
            <Button
                startIcon={<Add />}
                onClick={addPaymentMethod}
                sx={{ mt: 1 }}
            >
                Add New Payment
            </Button>
        </Box>

          {/* Totals */}
          <Box sx={{ mt: 2 }}>
            <Typography>Subtotal: {formatToMyanmarCurrency(totals.subtotal)}</Typography>
            <Typography>Tax: {formatToMyanmarCurrency(totals.tax)}</Typography>
            <Typography>Total: {formatToMyanmarCurrency(totals.total)}</Typography>
            <Typography>Paid: {formatToMyanmarCurrency(totals.totalPaid)}</Typography>
            <Typography>Balance: {formatToMyanmarCurrency(totals.balance)}</Typography>
          </Box>
         
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={validateAndSave}
              sx={{ mr: 2 }}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditModalOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default EditOrderModal;
